import { useMemo } from 'react';

// Park-Miller LCG — deterministic, same output every render.
function makeLCG(seed) {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

const DEG = Math.PI / 180;
const CX = 40;
const CY = 40;

const RINGS = [
  { r: 34,   sw: 0.75, seed: 2847, startDeg: -92 },
  { r: 28.5, sw: 0.90, seed: 5123, startDeg:  12 },
  { r: 23,   sw: 0.70, seed: 7491, startDeg: -28 },
];

function buildRing(r, seed, startDeg) {
  const rng = makeLCG(seed);
  const pieces = [];
  let deg = 0;

  while (deg < 360) {
    const roll = rng();
    const remaining = 360 - deg;

    if (roll < 0.22) {
      // Gap
      deg += 4 + rng() * 13;
    } else if (roll < 0.30) {
      // Dot
      const a = (startDeg + deg) * DEG;
      pieces.push({
        type: 'dot',
        x: CX + r * Math.cos(a),
        y: CY + r * Math.sin(a),
        op: 0.40 + rng() * 0.50,
      });
      deg += 2.5;
    } else {
      // Arc — short / medium / long
      let spanDeg;
      if      (roll < 0.54) spanDeg =  6 + rng() * 20;   // 6–26°
      else if (roll < 0.78) spanDeg = 26 + rng() * 50;   // 26–76°
      else                  spanDeg = 76 + rng() * 88;   // 76–164°

      spanDeg = Math.min(spanDeg, remaining - 2);
      if (spanDeg < 4) { deg += spanDeg; continue; }

      const peakOp = 0.48 + rng() * 0.52;           // 0.48–1.0
      const N = Math.max(1, Math.ceil(spanDeg / 3)); // sub-pieces
      const step = spanDeg / N;

      for (let k = 0; k < N; k++) {
        const t = (k + 0.5) / N;                    // 0→1 across segment
        const op = peakOp * Math.sin(t * Math.PI);  // fade in & out
        const a0 = (startDeg + deg + k * step) * DEG;
        const a1 = (startDeg + deg + (k + 1) * step) * DEG;
        const x0 = CX + r * Math.cos(a0);
        const y0 = CY + r * Math.sin(a0);
        const x1 = CX + r * Math.cos(a1);
        const y1 = CY + r * Math.sin(a1);
        pieces.push({
          type: 'arc',
          d: `M${x0.toFixed(3)},${y0.toFixed(3)}A${r},${r},0,0,1,${x1.toFixed(3)},${y1.toFixed(3)}`,
          op,
        });
      }

      deg += spanDeg + 1.5 + rng() * 4.5; // arc + trailing micro-gap
    }
  }

  return pieces;
}

// Pre-compute rings once at module load — they never change.
const RING_DATA = RINGS.map(({ r, seed, startDeg }) => buildRing(r, seed, startDeg));

export default function InveiraLogo({ size = 24, dark = true }) {
  const color = dark ? '#C8924A' : '#7A5520';

  // useMemo so JSX elements are stable when size/color don't change.
  const elements = useMemo(() => {
    const els = [];
    RINGS.forEach(({ sw }, ri) => {
      RING_DATA[ri].forEach((piece, pi) => {
        const key = `${ri}-${pi}`;
        if (piece.type === 'dot') {
          els.push(
            <circle
              key={key}
              cx={piece.x.toFixed(3)}
              cy={piece.y.toFixed(3)}
              r={(sw * 0.85).toFixed(3)}
              fill={color}
              opacity={piece.op.toFixed(3)}
            />
          );
        } else {
          els.push(
            <path
              key={key}
              d={piece.d}
              stroke={color}
              strokeWidth={sw}
              strokeLinecap="round"
              fill="none"
              opacity={piece.op.toFixed(3)}
            />
          );
        }
      });
    });
    return els;
  }, [color]);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {elements}
    </svg>
  );
}
